import { Route } from '@/Enums/Routes/Route.enum';
import { logoutCleanup } from '@/helpers/auth';
import { getStoredAuthToken } from '@/helpers/set-auth-token';
import store from '@/store';
import * as Sentry from '@sentry/react';
import axios, { HttpStatusCode } from 'axios';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const isAbortError = (error: unknown): boolean => {
  if (error instanceof Error && error.name === 'AbortError') return true;
  if (axios.isCancel(error)) return true;
  if (typeof error === 'object' && error !== null && 'code' in error && (error as any).code === 'ERR_CANCELED')
    return true;
  return false;
};

// 4xx responses are client errors (bad input, auth, missing data) — not
// infrastructure problems. Capturing them in Sentry creates noise that
// auto-generates GitHub issues via the n8n Sentry-to-GitHub workflow.
// Only 5xx (server errors) and network failures (no response) indicate
// something an engineer needs to investigate.
const isSentryReportableError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return true;
  if (isAbortError(error)) return false;
  const status = error.response?.status;
  if (!status) return true; // network error / no response — always report
  return status >= 500; // only 5xx server errors
};

export const AxiosProvider = () => {
  const navigate = useNavigate();

  useEffect(() => {
    axios.defaults.baseURL = import.meta.env.VITE_BASE_API_URL;
    axios.interceptors.request.use(request => {
      // Set auth token only if the request is going to our API
      if (request.url?.startsWith('/') || request.url?.startsWith(import.meta.env.VITE_BASE_API_URL)) {
        // Get auth token either from redux store or from localStorage
        const token = store.getState().auth.token || getStoredAuthToken();
        request.headers.Authorization = `Bearer ${token}`;
      }
      return request;
    });
    axios.interceptors.response.use(
      response => response,
      error => {
        if (isSentryReportableError(error)) {
          Sentry.captureException(error, {
            tags: {
              errorType: 'axios',
              statusCode: error?.response?.status,
            },
            extra: {
              url: error?.config?.url,
              method: error?.config?.method,
              responseData: error?.response?.data,
            },
          });
        }

        if (error?.response?.status == HttpStatusCode.Unauthorized && !error?.config?.url?.includes('/sso/auth')) {
          // Skip if the user is already logged out
          if (store.getState().auth.token) {
            toast.error('Unauthorized. Please login.');
            logoutCleanup();
            navigate(Route.LOGIN);
          }
        }
        return Promise.reject(error);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default AxiosProvider;
